import React, { useState, useEffect, useCallback } from 'react';
import FlashCard from './FlashCard';
import VocabularyMenu from './VocabularyMenu';
import { 
  getMemorizedCards, 
  addMemorizedCard, 
  resetMemorizedCards, 
  getMemoryStats 
} from './localStorage';
import { getDb, signInWithGoogle, signOut as signOutFn, subscribeToAuth } from './firebase';
import { collection, getDocs, query, orderBy, doc, updateDoc, addDoc, deleteDoc, setDoc, getDoc, onSnapshot, writeBatch, updateDoc as updateDocFs } from 'firebase/firestore';
// Local JSON data removed; Firestore is the sole data source now

const App = () => {
  const [selectedVocabulary, setSelectedVocabulary] = useState(null);
  const [vocabData, setVocabData] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [knownWords, setKnownWords] = useState(new Set());
  const [unknownWords, setUnknownWords] = useState(new Set());
  const [isAnimating, setIsAnimating] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [shuffledWords, setShuffledWords] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [dragDistance, setDragDistance] = useState(0);
  const [showEnglishFirst, setShowEnglishFirst] = useState(false);
  const [pulseDirection, setPulseDirection] = useState(null);
  const [cardExitDirection, setCardExitDirection] = useState(null);
  const [memorizedCards, setMemorizedCards] = useState(new Set());
  const [memoryStats, setMemoryStats] = useState({ memorizedCount: 0, remainingCount: 0, totalCount: 0, completionPercentage: 0 });
  const [user, setUser] = useState(null);
  const [isEditor, setIsEditor] = useState(false);
  const [isEditorByWindow, setIsEditorByWindow] = useState(false);
  const [isEditorByRules, setIsEditorByRules] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [isLoadingSet, setIsLoadingSet] = useState(false);
  const [editValues, setEditValues] = useState({ swedish: '', english: '', pronunciation: '', type: '', article: '', plural: '', preteritum: '' });
  const [isTableView, setIsTableView] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalWord, setModalWord] = useState(null);
  const [modalEditValues, setModalEditValues] = useState({ swedish: '', english: '', pronunciation: '', type: '', article: '', plural: '', preteritum: '' });
  const [isDeleteSetOpen, setIsDeleteSetOpen] = useState(false);
  const [isDeletingSet, setIsDeletingSet] = useState(false);

  const db = getDb();

  // Shuffle array function
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Auth subscription and editor role resolution
  useEffect(() => {
    const unsub = subscribeToAuth(async (u) => {
      setUser(u);
      const list = (typeof window !== 'undefined' && Array.isArray(window.__FIREBASE_EDITORS__)) ? window.__FIREBASE_EDITORS__ : [];
      const byWindow = !!u && list.length > 0 && list.includes(u.uid);
      let byRules = false;
      try {
        if (u && db) {
          const cfg = await getDoc(doc(db, 'config', 'editors'));
          const uids = cfg.exists() ? cfg.data().uids || [] : [];
          console.debug('[RoleCheck:getDoc]', { uid: u.uid, cfgExists: cfg.exists(), uids });
          byRules = Array.isArray(uids) && uids.includes(u.uid);
        }
      } catch (_) {}
      setIsEditorByWindow(byWindow);
      setIsEditorByRules(byRules);
      setIsEditor(!!u && (byWindow || byRules));
    });
    return () => { if (unsub) unsub(); };
  }, [db]);

  // Live subscribe to editors doc so role updates without refresh
  useEffect(() => {
    if (!db || !user) return;
    const unsubscribe = onSnapshot(doc(db, 'config', 'editors'), (snapshot) => {
      const uids = snapshot.exists() ? snapshot.data().uids || [] : [];
      console.debug('[RoleCheck:onSnapshot]', { uid: user.uid, snapshotExists: snapshot.exists(), uids });
      const byRules = Array.isArray(uids) && uids.includes(user.uid);
      setIsEditorByRules(byRules);
      setIsEditor(!!user && (isEditorByWindow || byRules));
    }, () => {
      // ignore errors
    });
    return () => unsubscribe();
  }, [db, user, isEditorByWindow]);

  // Load set data from Firestore or fallback
  const loadSetData = async (setId) => {
    if (!db) return [];
    try {
      const q = query(collection(db, `vocabSets/${setId}/words`), orderBy('order'));
      const snap = await getDocs(q);
      const words = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      return words;
    } catch (e) {
      console.warn('Failed to load from Firestore:', e);
      return [];
    }
  };

  // Handle vocabulary selection
  const handleVocabularySelect = (vocabularyOption) => {
    setSelectedVocabulary(vocabularyOption);
    setIsLoadingSet(true);
    setIsTableView(false);
    // Reset basic state
    setCurrentIndex(0);
    setKnownWords(new Set());
    setUnknownWords(new Set());
    setIsCompleted(false);
    setIsDragging(false);
    setDragDistance(0);
    setPulseDirection(null);
    // history
    window.history.pushState({ view: 'flashcards', vocabulary: vocabularyOption.id }, '', `#${vocabularyOption.id}`);
    // async load
    (async () => {
      const data = await loadSetData(vocabularyOption.id);
      const memorized = getMemorizedCards(vocabularyOption.id);
      setMemorizedCards(memorized);
      const nonMemorizedData = data.filter(word => !memorized.has(word.id));
      setMemoryStats(getMemoryStats(vocabularyOption.id, data.length));
      setVocabData(data);
      setShuffledWords(shuffleArray(nonMemorizedData));
      setIsCompleted(nonMemorizedData.length === 0);
      setIsLoadingSet(false);
    })();
  };

  // Return to menu
  const handleBackToMenu = useCallback(() => {
    setSelectedVocabulary(null);
    setVocabData([]);
    setShuffledWords([]);
    setCurrentIndex(0);
    setKnownWords(new Set());
    setUnknownWords(new Set());
    setIsCompleted(false);
    setMemorizedCards(new Set());
    setMemoryStats({ memorizedCount: 0, remainingCount: 0, totalCount: 0, completionPercentage: 0 });
    
    // Update URL when manually going back to menu
    if (window.location.hash !== '#menu') {
      window.history.pushState({ view: 'menu' }, '', '#menu');
    }
  }, []);

  const currentWord = shuffledWords[currentIndex];
  const totalWords = shuffledWords.length;
  const progress = totalWords > 0 ? ((currentIndex) / totalWords) * 100 : 0;

  const handleSwipe = (direction) => {
    if (isAnimating || !currentWord) return;

    setIsAnimating(true);
    
    // Clear drag glow immediately when swipe occurs
    setIsDragging(false);
    setDragDistance(0);
    
    // Trigger pulse feedback and card exit animation
    const exitDir = direction === 'know' ? 'right' : 'left';
    setPulseDirection(exitDir);
    setCardExitDirection(exitDir);
    
    // Add word to appropriate set and handle memory persistence
    if (direction === 'know') {
      setKnownWords(prev => new Set([...prev, currentWord.id]));
      setUnknownWords(prev => {
        const newSet = new Set(prev);
        newSet.delete(currentWord.id);
        return newSet;
      });
      
      // Save to localStorage and update memorized cards
      const updatedMemorized = addMemorizedCard(selectedVocabulary.id, currentWord.id);
      setMemorizedCards(updatedMemorized);
      
      // Update memory stats
      const stats = getMemoryStats(selectedVocabulary.id, vocabData.length);
      setMemoryStats(stats);
      
    } else if (direction === 'dont-know') {
      setUnknownWords(prev => new Set([...prev, currentWord.id]));
      setKnownWords(prev => {
        const newSet = new Set(prev);
        newSet.delete(currentWord.id);
        return newSet;
      });
    }

    // Clear pulse after animation completes
    setTimeout(() => {
      setPulseDirection(null);
    }, 600);

    // Move to next card after exit animation completes
    setTimeout(() => {
      setCardExitDirection(null);
      if (currentIndex + 1 >= totalWords) {
        setIsCompleted(true);
      } else {
        setCurrentIndex(prev => prev + 1);
      }
      setIsAnimating(false);
    }, 300);
  };

  const handleControlClick = (action) => {
    if (action === 'flip') {
      // Trigger flip by simulating a click on the card
      const flashCard = document.querySelector('.flash-card');
      if (flashCard && !isAnimating) {
        flashCard.click();
      }
      return;
    }
    handleSwipe(action);
  };

  const handleDragUpdate = (dragging, distance) => {
    setIsDragging(dragging);
    setDragDistance(distance);
  };

  const restartSession = () => {
    // Filter out memorized cards for restart
    const nonMemorizedData = vocabData.filter(word => !memorizedCards.has(word.id));
    
    setCurrentIndex(0);
    setKnownWords(new Set());
    setUnknownWords(new Set());
    setIsCompleted(nonMemorizedData.length === 0);
    setShuffledWords(shuffleArray(nonMemorizedData));
  };
  
  // Reset memory for current vocabulary set
  const resetMemory = () => {
    if (!selectedVocabulary) return;
    
    const clearedMemory = resetMemorizedCards(selectedVocabulary.id);
    setMemorizedCards(clearedMemory);
    
    // Update memory stats
    const stats = getMemoryStats(selectedVocabulary.id, vocabData.length);
    setMemoryStats(stats);
    
    // Restart with all cards available
    setCurrentIndex(0);
    setKnownWords(new Set());
    setUnknownWords(new Set());
    setIsCompleted(false);
    setShuffledWords(shuffleArray(vocabData));
  };

  const goToPrevious = () => {
    if (currentIndex > 0 && !isAnimating) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const goToNext = () => {
    if (currentIndex < totalWords - 1 && !isAnimating) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  // Keep edit form in sync with current card
  useEffect(() => {
    if (currentWord) {
      setEditValues({
        swedish: currentWord.swedish || '',
        english: currentWord.english || '',
        pronunciation: currentWord.pronunciation || '',
        type: currentWord.type || '',
        article: currentWord.article || '',
        plural: currentWord.plural || '',
        preteritum: (currentWord.preteritum === null ? '' : (currentWord.preteritum || ''))
      });
    }
  }, [currentWord]);

  // Editor actions
  const saveCurrentWord = async () => {
    if (!db || !selectedVocabulary || !currentWord) return;
    try {
      await updateDoc(doc(db, `vocabSets/${selectedVocabulary.id}/words/${currentWord.id}`), { ...editValues });
      // update local state
      setVocabData(prev => prev.map(w => w.id === currentWord.id ? { ...w, ...editValues } : w));
      setShuffledWords(prev => prev.map(w => w.id === currentWord.id ? { ...w, ...editValues } : w));
    } catch (e) {
      console.error('Save failed:', e);
      alert('Save failed. See console for details.');
    }
  };

  const deleteCurrentWord = async () => {
    if (!db || !selectedVocabulary || !currentWord) return;
    if (!window.confirm('Delete this word?')) return;
    try {
      await deleteDoc(doc(db, `vocabSets/${selectedVocabulary.id}/words/${currentWord.id}`));
      const newData = vocabData.filter(w => w.id !== currentWord.id);
      setVocabData(newData);
      const newShuffled = shuffledWords.filter(w => w.id !== currentWord.id);
      setShuffledWords(newShuffled);
      setIsCompleted(newShuffled.length === 0);
      setMemoryStats(getMemoryStats(selectedVocabulary.id, newData.length));
      // update set wordCount down by 1
      try {
        await updateDocFs(doc(db, 'vocabSets', selectedVocabulary.id), { wordCount: newData.length });
      } catch (_) {}
    } catch (e) {
      console.error('Delete failed:', e);
      alert('Delete failed. See console for details.');
    }
  };

  const saveAsNewWord = async () => {
    if (!db || !selectedVocabulary) return;
    try {
      const payload = { ...editValues, order: vocabData.length };
      const newDoc = await addDoc(collection(db, `vocabSets/${selectedVocabulary.id}/words`), payload);
      const newWord = { id: newDoc.id, ...payload };
      const newData = [...vocabData, newWord];
      setVocabData(newData);
      setShuffledWords([...shuffledWords, newWord]);
      setMemoryStats(getMemoryStats(selectedVocabulary.id, newData.length));
      // update set wordCount up by 1
      try {
        await updateDocFs(doc(db, 'vocabSets', selectedVocabulary.id), { wordCount: newData.length });
      } catch (_) {}
    } catch (e) {
      console.error('Add failed:', e);
      alert('Add failed. See console for details.');
    }
  };

  const deleteCurrentSet = async () => {
    if (!db || !selectedVocabulary) return;
    setIsDeletingSet(true);
    try {
      // Firestore does not delete subcollections with their parent, so remove the words first
      const wordsCol = collection(db, `vocabSets/${selectedVocabulary.id}/words`);
      const snap = await getDocs(wordsCol);
      for (let i = 0; i < snap.docs.length; i += 450) {
        const batch = writeBatch(db);
        snap.docs.slice(i, i + 450).forEach(d => batch.delete(d.ref));
        await batch.commit();
      }
      await deleteDoc(doc(db, 'vocabSets', selectedVocabulary.id));
      resetMemorizedCards(selectedVocabulary.id);
      setIsDeleteSetOpen(false);
      setIsTableView(false);
      handleBackToMenu();
    } catch (e) {
      console.error('Delete set failed:', e);
      alert('Delete set failed. See console for details.');
    } finally {
      setIsDeletingSet(false);
    }
  };

  // Handle keyboard navigation (only during flashcards and not while typing)
  useEffect(() => {
    if (!selectedVocabulary || isTableView) return; // don't attach on menu/home or in table view

    const handleKeyPress = (e) => {
      // Ignore when typing in inputs/textareas/contenteditable
      const target = e.target;
      const tag = target && target.tagName ? target.tagName.toLowerCase() : '';
      const isEditable = (target && (target.isContentEditable || tag === 'input' || tag === 'textarea' || tag === 'select'));
      if (isEditable) return;

      if (isCompleted) return;
      
      switch (e.key) {
        case 'ArrowLeft':
          handleSwipe('dont-know');
          break;
        case 'ArrowRight':
          handleSwipe('know');
          break;
        case 'ArrowUp':
          e.preventDefault();
          goToPrevious();
          break;
        case 'ArrowDown':
          e.preventDefault();
          goToNext();
          break;
        case ' ':
          // prevent default only if not typing in inputs
          e.preventDefault();
          // Trigger flip by simulating a click on the card
          const flashCard = document.querySelector('.flash-card');
          if (flashCard && !isAnimating) {
            flashCard.click();
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [selectedVocabulary, isTableView, currentIndex, isCompleted, isAnimating, totalWords]);

  // Table edit modal helpers
  const openEditModal = (word) => {
    setModalWord(word);
    setModalEditValues({
      swedish: word.swedish || '',
      english: word.english || '',
      pronunciation: word.pronunciation || '',
      type: word.type || '',
      article: word.article || '',
      plural: word.plural || '',
      preteritum: word.preteritum || ''
    });
    setIsModalOpen(true);
  };

  const closeEditModal = () => {
    setIsModalOpen(false);
    setModalWord(null);
  };

  const saveModalWord = async () => {
    if (!db || !selectedVocabulary || !modalWord) return;
    try {
      await updateDoc(doc(db, `vocabSets/${selectedVocabulary.id}/words/${modalWord.id}`), { ...modalEditValues });
      setVocabData(prev => prev.map(w => w.id === modalWord.id ? { ...w, ...modalEditValues } : w));
      setShuffledWords(prev => prev.map(w => w.id === modalWord.id ? { ...w, ...modalEditValues } : w));
      closeEditModal();
    } catch (e) {
      console.error('Save failed:', e);
      alert('Save failed. See console for details.');
    }
  };

  const saveModalAsNewWord = async () => {
    if (!db || !selectedVocabulary) return;
    try {
      const payload = { ...modalEditValues, order: vocabData.length };
      const newDocRef = await addDoc(collection(db, `vocabSets/${selectedVocabulary.id}/words`), payload);
      const newWord = { id: newDocRef.id, ...payload };
      const newData = [...vocabData, newWord];
      setVocabData(newData);
      setShuffledWords([...shuffledWords, newWord]);
      setMemoryStats(getMemoryStats(selectedVocabulary.id, newData.length));
      try {
        await updateDocFs(doc(db, 'vocabSets', selectedVocabulary.id), { wordCount: newData.length });
      } catch (_) {}
      closeEditModal();
    } catch (e) {
      console.error('Add failed:', e);
      alert('Add failed. See console for details.');
    }
  };

  const deleteModalWord = async () => {
    if (!db || !selectedVocabulary || !modalWord) return;
    if (!window.confirm('Delete this word?')) return;
    try {
      await deleteDoc(doc(db, `vocabSets/${selectedVocabulary.id}/words/${modalWord.id}`));
      const newData = vocabData.filter(w => w.id !== modalWord.id);
      setVocabData(newData);
      setShuffledWords(prev => prev.filter(w => w.id !== modalWord.id));
      setMemoryStats(getMemoryStats(selectedVocabulary.id, newData.length));
      try {
        await updateDocFs(doc(db, 'vocabSets', selectedVocabulary.id), { wordCount: newData.length });
      } catch (_) {}
      closeEditModal();
    } catch (e) {
      console.error('Delete failed:', e);
      alert('Delete failed. See console for details.');
    }
  };

  // History guard to prevent leaving app unintentionally
  useEffect(() => {
    let historyGuardSetup = false;

    const setupHistoryGuard = () => {
      if (!historyGuardSetup) {
        // Push a guard entry immediately when app loads
        window.history.pushState({ guard: true, view: 'menu' }, '', '#menu');
        historyGuardSetup = true;
      }
    };

    const handlePopState = (event) => {
      // If we're in flashcards and user clicked back
      if (selectedVocabulary) {
        // Go back to menu
        handleBackToMenu();
        // Push menu state to maintain history
        window.history.pushState({ view: 'menu' }, '', '#menu');
        return;
      }

      // If we're on menu and user clicked back, and this isn't the guard entry
      if (!selectedVocabulary && event.state && !event.state.guard) {
        // Let them leave the app (this will go to previous external page)
        return;
      }

      // If they're trying to go back from the guard entry, prevent it
      if (event.state && event.state.guard) {
        window.history.pushState({ view: 'menu' }, '', '#menu');
      }
    };

    // Set up the guard on first load
    if (!selectedVocabulary) {
      setupHistoryGuard();
    }

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [selectedVocabulary, handleBackToMenu]);

  // Show menu if no vocabulary is selected
  if (!selectedVocabulary) {
    return (
      <>
        <div style={{ position: 'fixed', top: '10px', right: '10px', display: 'flex', gap: '8px', alignItems: 'center' }}>
          {user ? (
            <>
              {isEditor && (
                <label style={{ fontSize: '12px' }}>
                  <input type="checkbox" checked={editMode} onChange={(e) => setEditMode(e.target.checked)} /> Edit mode
                </label>
              )}
              <button onClick={signOutFn}>Sign out</button>
            </>
          ) : (
            <button onClick={signInWithGoogle}>Sign in</button>
          )}
        </div>
        <VocabularyMenu onSelectVocabulary={handleVocabularySelect} isEditor={isEditor} editMode={editMode} />
      </>
    );
  }

  if (isLoadingSet || shuffledWords.length === 0) {
    return <div className="app">Loading...</div>;
  }

  if (isCompleted) {
    const accuracy = totalWords > 0 ? Math.round((knownWords.size / totalWords) * 100) : 0;
    const allCardsMemorized = memoryStats.remainingCount === 0;
    
    return (
      <div className="app">
        <div className="completion-screen">
          {allCardsMemorized ? (
            <>
              <h2>🎓 Course Mastered!</h2>
              <p>Congratulations! You've memorized all cards in this set!</p>
            </>
          ) : (
            <>
              <h2>🎉 Session Complete!</h2>
              <p>Great job studying Swedish vocabulary!</p>
            </>
          )}
          
          <div className="stats" style={{ marginBottom: '30px', justifyContent: 'center' }}>
            <div className="stat-item">
              <span className="stat-number">{memoryStats.memorizedCount}</span>
              <span className="stat-label">Memorized</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{memoryStats.remainingCount}</span>
              <span className="stat-label">Remaining</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{memoryStats.completionPercentage}%</span>
              <span className="stat-label">Progress</span>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
            {!allCardsMemorized && (
              <button className="restart-btn" onClick={restartSession}>
                Study Again
              </button>
            )}
            <button 
              className="restart-btn" 
              onClick={resetMemory}
              style={{ backgroundColor: '#ff6b6b' }}
            >
              Reset Memory
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      {/* Auth + edit controls */}
      <div style={{ position: 'fixed', top: '10px', right: '10px', display: 'flex', gap: '12px', alignItems: 'center' }}>
        {user ? (
          <>
            {isEditor && selectedVocabulary && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '12px', opacity: 0.8 }}>Edit</span>
                <label style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <input
                    type="checkbox"
                    checked={editMode}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setEditMode(on);
                      if (on) setIsTableView(false);
                    }}
                  />
                  word
                </label>
                <label style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <input
                    type="checkbox"
                    checked={isTableView}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setIsTableView(on);
                      if (on) setEditMode(false);
                    }}
                  />
                  all
                </label>
              </div>
            )}
            {/* Seed button removed: role is driven by Firestore __config__/editors */}
            <button onClick={signOutFn}>Sign out</button>
          </>
        ) : (
          <button onClick={signInWithGoogle}>Sign in</button>
        )}
      </div>
      {/* Back to menu button */}
      <button className="back-to-menu" onClick={handleBackToMenu} title="Back to menu">
        ←
      </button>
      {/* Full screen glow effects - always rendered for smooth transitions */}
      <div className={`page-glow page-glow-right ${isDragging && dragDistance > 50 ? 'page-glow-active' : ''}`}></div>
      <div className={`page-glow page-glow-left ${isDragging && dragDistance < -50 ? 'page-glow-active' : ''}`}></div>
      
      {/* Pulse feedback glow effects */}
      <div className={`pulse-glow pulse-glow-right ${pulseDirection === 'right' ? 'pulse-active' : ''}`}></div>
      <div className={`pulse-glow pulse-glow-left ${pulseDirection === 'left' ? 'pulse-active' : ''}`}></div>
      
      {/* Language toggle */}
      <div className="language-toggle">
        <label className="toggle-label">
          <span className={!showEnglishFirst ? 'active' : ''}>🇸🇪</span>
          <div className="toggle-switch">
            <input 
              type="checkbox" 
              checked={showEnglishFirst}
              onChange={(e) => setShowEnglishFirst(e.target.checked)}
            />
            <span className="slider"></span>
          </div>
          <span className={showEnglishFirst ? 'active' : ''}>🇬🇧</span>
        </label>
      </div>

      <div className="progress-bar">
        <div 
          className="progress-fill" 
          style={{ width: `${progress}%` }}
        ></div>
      </div>

      {!isTableView && (
        <FlashCard 
          word={currentWord}
          onSwipe={handleSwipe}
          isAnimating={isAnimating}
          onDragUpdate={handleDragUpdate}
          showEnglishFirst={showEnglishFirst}
          exitDirection={cardExitDirection}
        />
      )}

      {isTableView && (
        <div style={{ width: '100%', maxWidth: '960px', marginTop: '10px' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
            <button onClick={() => setIsDeleteSetOpen(true)} style={{ padding: '8px 12px', fontSize: '13px', border: 'none', borderRadius: '6px', background: '#dc3545', color: '#fff', cursor: 'pointer' }}>Delete this set</button>
          </div>
          {isDeleteSetOpen && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => { if (!isDeletingSet) setIsDeleteSetOpen(false); }}>
              <div style={{ background: '#fff', color: '#212529', borderRadius: '10px', width: 'min(420px, 92vw)', padding: '16px', boxShadow: '0 10px 24px rgba(0,0,0,0.18)' }} onClick={(e) => e.stopPropagation()}>
                <h3 style={{ marginTop: 0, marginBottom: '8px' }}>Delete vocabulary set?</h3>
                <p style={{ margin: '0 0 16px 0' }}>
                  "{selectedVocabulary.title || selectedVocabulary.id}" and all of its {vocabData.length} words will be permanently deleted. This cannot be undone.
                </p>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button disabled={isDeletingSet} onClick={() => setIsDeleteSetOpen(false)} style={{ flex: 1, padding: '12px 14px', fontSize: '14px', border: '1px solid #cfd4da', borderRadius: '8px', background: '#fff', color: '#212529', cursor: 'pointer' }}>Cancel</button>
                  <button disabled={isDeletingSet} onClick={deleteCurrentSet} style={{ flex: 1, padding: '12px 14px', fontSize: '14px', fontWeight: 600, border: 'none', borderRadius: '8px', background: '#dc3545', color: '#fff', cursor: 'pointer' }}>{isDeletingSet ? 'Deleting…' : 'DELETE'}</button>
                </div>
              </div>
            </div>
          )}
          <div style={{ overflowX: 'auto', border: '1px solid #e9ecef', borderRadius: '8px' }}>
            <table style={{ width: '100%', minWidth: '1100px', borderCollapse: 'collapse', fontSize: '14px', color: '#e4e6ea' }}>
              <thead>
                <tr style={{ background: '#e9ecef' }}>
                  <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #e9ecef', color: '#212529', fontWeight: 600 }}>ID</th>
                  <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #e9ecef', color: '#212529', fontWeight: 600 }}>Swedish</th>
                  <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #e9ecef', color: '#212529', fontWeight: 600 }}>English</th>
                  <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #e9ecef', color: '#212529', fontWeight: 600 }}>Pronunciation</th>
                  <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #e9ecef', color: '#212529', fontWeight: 600 }}>Type</th>
                  <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #e9ecef', color: '#212529', fontWeight: 600 }}>Article</th>
                  <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #e9ecef', color: '#212529', fontWeight: 600 }}>Plural</th>
                  <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #e9ecef', color: '#212529', fontWeight: 600 }}>Preteritum</th>
                  <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #e9ecef', color: '#212529', fontWeight: 600 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {vocabData.map(w => (
                  <tr key={w.id}>
                    <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f3f5' }}>{String(w.id)}</td>
                    <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f3f5' }}>
                      {typeof w.swedish === 'object' && w.swedish !== null ? (
                        <div style={{ lineHeight: 1.2 }}>
                          {w.swedish.infinitive && (<div><strong>INF</strong>: {w.swedish.infinitive}</div>)}
                          {w.swedish.imperative && (<div><strong>IMP</strong>: {w.swedish.imperative}</div>)}
                          {w.swedish.present && (<div><strong>PRES</strong>: {w.swedish.present}</div>)}
                          {w.swedish.preteritum && (<div><strong>PRET</strong>: {w.swedish.preteritum}</div>)}
                          {w.swedish.supinum && (<div><strong>SUP</strong>: {w.swedish.supinum}</div>)}
                        </div>
                      ) : (
                        w.swedish
                      )}
                    </td>
                    <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f3f5' }}>{w.english}</td>
                    <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f3f5' }}>{w.pronunciation}</td>
                    <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f3f5' }}>{w.type ?? ''}</td>
                    <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f3f5' }}>{w.article ?? ''}</td>
                    <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f3f5' }}>{w.plural ?? ''}</td>
                    <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f3f5' }}>{w.preteritum ?? ''}</td>
                    <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f3f5' }}>
                      <button onClick={() => openEditModal(w)} style={{ padding: '8px 10px', fontSize: '13px', border: 'none', borderRadius: '6px', background: '#0d6efd', color: '#fff' }}>Edit</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Editor panel */}
      {editMode && !isTableView && (
        <div style={{
          marginTop: '10px',
          marginBottom: '16px',
          padding: '16px',
          border: '1px solid #cfd4da',
          borderRadius: '10px',
          background: '#ffffff',
          color: '#212529',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          maxWidth: '640px',
          width: '100%'
        }}>
          <h3 style={{ marginTop: 0, marginBottom: '5px' }}>Edit or add new word</h3>
          {!db && (
            <div style={{ color: '#b00020', marginBottom: '8px' }}>
              Firebase not configured. Provide window.__FIREBASE_CONFIG__.
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '10px', alignItems: 'center' }}>
            <label>ID</label>
            <input
              style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #cfd4da', borderRadius: '8px', background: '#f1f3f5', color: '#495057' }}
              value={(currentWord && currentWord.id) ? String(currentWord.id) : ''}
              disabled
            />
            <label>Swedish</label>
            {(editValues.type === 'verb' || editValues.type === 'adjective') ? (
              <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '10px', alignItems: 'center', gridColumn: '1 / -1' }}>
                <label>Imperative</label>
                <input style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #cfd4da', borderRadius: '8px' }} value={(editValues.swedish && editValues.swedish.imperative) || ''} onChange={(e) => setEditValues(v => ({ ...v, swedish: { ...(typeof v.swedish === 'object' && v.swedish ? v.swedish : {}), imperative: e.target.value } }))} />
                <label>Infinitive</label>
                <input style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #cfd4da', borderRadius: '8px' }} value={(editValues.swedish && editValues.swedish.infinitive) || ''} onChange={(e) => setEditValues(v => ({ ...v, swedish: { ...(typeof v.swedish === 'object' && v.swedish ? v.swedish : {}), infinitive: e.target.value } }))} />
                <label>Present</label>
                <input style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #cfd4da', borderRadius: '8px' }} value={(editValues.swedish && editValues.swedish.present) || ''} onChange={(e) => setEditValues(v => ({ ...v, swedish: { ...(typeof v.swedish === 'object' && v.swedish ? v.swedish : {}), present: e.target.value } }))} />
                <label>Preteritum</label>
                <input style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #cfd4da', borderRadius: '8px' }} value={(editValues.swedish && editValues.swedish.preteritum) || ''} onChange={(e) => setEditValues(v => ({ ...v, swedish: { ...(typeof v.swedish === 'object' && v.swedish ? v.swedish : {}), preteritum: e.target.value } }))} />
                <label>Supinum</label>
                <input style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #cfd4da', borderRadius: '8px' }} value={(editValues.swedish && editValues.swedish.supinum) || ''} onChange={(e) => setEditValues(v => ({ ...v, swedish: { ...(typeof v.swedish === 'object' && v.swedish ? v.swedish : {}), supinum: e.target.value } }))} />
              </div>
            ) : (
              <input
                style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #cfd4da', borderRadius: '8px', background: '#fff', color: '#212529' }}
                value={editValues.swedish}
                onChange={(e) => setEditValues(v => ({ ...v, swedish: e.target.value }))}
              />
            )}
            <label>English</label>
            <input
              style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #cfd4da', borderRadius: '8px', background: '#fff', color: '#212529' }}
              value={editValues.english}
              onChange={(e) => setEditValues(v => ({ ...v, english: e.target.value }))}
            />
            <label>Pronunciation</label>
            <input
              style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #cfd4da', borderRadius: '8px', background: '#fff', color: '#212529' }}
              value={editValues.pronunciation}
              onChange={(e) => setEditValues(v => ({ ...v, pronunciation: e.target.value }))}
            />
            <label>Type</label>
            <input
              style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #cfd4da', borderRadius: '8px', background: '#fff', color: '#212529' }}
              value={editValues.type}
              onChange={(e) => setEditValues(v => ({ ...v, type: e.target.value }))}
            />
            <label>Article</label>
            <input
              style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #cfd4da', borderRadius: '8px', background: '#fff', color: '#212529' }}
              value={editValues.article}
              onChange={(e) => setEditValues(v => ({ ...v, article: e.target.value }))}
            />
            <label>Plural</label>
            <input
              style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #cfd4da', borderRadius: '8px', background: '#fff', color: '#212529' }}
              value={editValues.plural || ''}
              onChange={(e) => setEditValues(v => ({ ...v, plural: e.target.value }))}
            />
            <label>Preteritum</label>
            <input
              style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #cfd4da', borderRadius: '8px', background: '#fff', color: '#212529' }}
              value={editValues.preteritum || ''}
              onChange={(e) => setEditValues(v => ({ ...v, preteritum: e.target.value }))}
            />
          </div>
          <div style={{ display: 'flex', gap: '10px', marginTop: '14px', flexWrap: 'wrap' }}>
            <button
              disabled={!db}
              onClick={saveCurrentWord}
              style={{ flex: 1, padding: '12px 14px', fontSize: '14px', border: 'none', borderRadius: '8px', background: '#0d6efd', color: '#fff' }}
            >
              Save
            </button>
            <button
              disabled={!db}
              onClick={deleteCurrentWord}
              style={{ flex: 1, padding: '12px 14px', fontSize: '14px', border: 'none', borderRadius: '8px', backgroundColor: '#ff6b6b', color: '#fff' }}
            >
              Delete
            </button>
            <button
              disabled={!db}
              onClick={saveAsNewWord}
              style={{ flex: 1, padding: '12px 14px', fontSize: '14px', border: 'none', borderRadius: '8px', background: '#6c757d', color: '#fff' }}
            >
              Save as new word
            </button>
          </div>
        </div>
      )}

      {/* Modal for table row edit */}
      {isModalOpen && isEditor && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={closeEditModal}>
          <div style={{ background: '#fff', color: '#212529', borderRadius: '10px', width: 'min(640px, 92vw)', padding: '16px', boxShadow: '0 10px 24px rgba(0,0,0,0.18)' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, marginBottom: '5px' }}>Edit word</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '10px', alignItems: 'center' }}>
              <label>ID</label>
              <input
                style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #cfd4da', borderRadius: '8px', background: '#f1f3f5', color: '#495057' }}
                value={(modalWord && modalWord.id) ? String(modalWord.id) : ''}
                disabled
              />
              <label>Swedish</label>
              {(modalEditValues.type === 'verb' || modalEditValues.type === 'adjective') ? (
              <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '10px', alignItems: 'center', gridColumn: '1 / -1' }}>
                <label>Imperative</label>
                <input style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #cfd4da', borderRadius: '8px' }} value={(modalEditValues.swedish && modalEditValues.swedish.imperative) || ''} onChange={(e) => setModalEditValues(v => ({ ...v, swedish: { ...(typeof v.swedish === 'object' && v.swedish ? v.swedish : {}), imperative: e.target.value } }))} />
                <label>Infinitive</label>
                <input style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #cfd4da', borderRadius: '8px' }} value={(modalEditValues.swedish && modalEditValues.swedish.infinitive) || ''} onChange={(e) => setModalEditValues(v => ({ ...v, swedish: { ...(typeof v.swedish === 'object' && v.swedish ? v.swedish : {}), infinitive: e.target.value } }))} />
                <label>Present</label>
                <input style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #cfd4da', borderRadius: '8px' }} value={(modalEditValues.swedish && modalEditValues.swedish.present) || ''} onChange={(e) => setModalEditValues(v => ({ ...v, swedish: { ...(typeof v.swedish === 'object' && v.swedish ? v.swedish : {}), present: e.target.value } }))} />
                <label>Preteritum</label>
                <input style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #cfd4da', borderRadius: '8px' }} value={(modalEditValues.swedish && modalEditValues.swedish.preteritum) || ''} onChange={(e) => setModalEditValues(v => ({ ...v, swedish: { ...(typeof v.swedish === 'object' && v.swedish ? v.swedish : {}), preteritum: e.target.value } }))} />
                <label>Supinum</label>
                <input style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #cfd4da', borderRadius: '8px' }} value={(modalEditValues.swedish && modalEditValues.swedish.supinum) || ''} onChange={(e) => setModalEditValues(v => ({ ...v, swedish: { ...(typeof v.swedish === 'object' && v.swedish ? v.swedish : {}), supinum: e.target.value } }))} />
              </div>
            ) : (
              <input style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #cfd4da', borderRadius: '8px' }} value={typeof modalEditValues.swedish === 'string' ? modalEditValues.swedish : ''} onChange={(e) => setModalEditValues(v => ({ ...v, swedish: e.target.value }))} />
            )}
              <label>English</label>
              <input style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #cfd4da', borderRadius: '8px' }} value={modalEditValues.english} onChange={(e) => setModalEditValues(v => ({ ...v, english: e.target.value }))} />
              <label>Pronunciation</label>
              <input style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #cfd4da', borderRadius: '8px' }} value={modalEditValues.pronunciation} onChange={(e) => setModalEditValues(v => ({ ...v, pronunciation: e.target.value }))} />
              <label>Type</label>
              <input style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #cfd4da', borderRadius: '8px' }} value={modalEditValues.type} onChange={(e) => setModalEditValues(v => ({ ...v, type: e.target.value }))} />
              <label>Article</label>
              <input style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #cfd4da', borderRadius: '8px' }} value={modalEditValues.article} onChange={(e) => setModalEditValues(v => ({ ...v, article: e.target.value }))} />
              <label>Plural</label>
              <input style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #cfd4da', borderRadius: '8px' }} value={modalEditValues.plural || ''} onChange={(e) => setModalEditValues(v => ({ ...v, plural: e.target.value }))} />
              <label>Preteritum</label>
              <input style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #cfd4da', borderRadius: '8px' }} value={modalEditValues.preteritum || ''} onChange={(e) => setModalEditValues(v => ({ ...v, preteritum: e.target.value }))} />
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '14px', justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button onClick={closeEditModal} style={{ padding: '10px 14px', fontSize: '14px', border: '1px solid #cfd4da', borderRadius: '8px', background: '#fff', color: '#212529' }}>Cancel</button>
                <button onClick={deleteModalWord} style={{ padding: '10px 14px', fontSize: '14px', border: 'none', borderRadius: '8px', background: '#ff6b6b', color: '#fff' }}>Delete</button>
              </div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button onClick={saveModalAsNewWord} style={{ padding: '10px 14px', fontSize: '14px', border: 'none', borderRadius: '8px', background: '#6c757d', color: '#fff' }}>Save as new word</button>
                <button onClick={saveModalWord} style={{ padding: '10px 14px', fontSize: '14px', border: 'none', borderRadius: '8px', background: '#0d6efd', color: '#fff' }}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="controls">
        <button 
          className="control-btn dont-know" 
          onClick={() => handleControlClick('dont-know')}
          disabled={isAnimating}
          title="Don't know (←)"
        >
          ✗
        </button>
        
        <button 
          className="control-btn flip-btn"
          onClick={() => handleControlClick('flip')}
          disabled={isAnimating}
          title="Flip card (Space)"
        >
          🔄
        </button>
        
        <button 
          className="control-btn know" 
          onClick={() => handleControlClick('know')}
          disabled={isAnimating}
          title="I know this (→)"
        >
          ✓
        </button>
      </div>

      <div className="stats">
        <div className="stat-item">
          <span className="stat-number">{currentIndex + 1}/{totalWords}</span>
          <span className="stat-label">Session</span>
        </div>
        <div className="stat-item">
          <span className="stat-number">{memoryStats.memorizedCount}</span>
          <span className="stat-label">Memorized</span>
        </div>
        <div className="stat-item">
          <span className="stat-number">{memoryStats.remainingCount}</span>
          <span className="stat-label">Remaining</span>
        </div>
        <div className="stat-item">
          <span className="stat-number">{memoryStats.completionPercentage}%</span>
          <span className="stat-label">Progress</span>
        </div>
      </div>
      
      {/* Memory reset link - always reserve space */}
      <div style={{ textAlign: 'center', marginTop: '10px', minHeight: '20px' }}>
        {memoryStats.memorizedCount > 0 ? (
          <button 
            onClick={resetMemory}
            style={{ 
              background: 'none',
              border: 'none',
              color: '#6c757d',
              fontSize: '12px',
              textDecoration: 'underline',
              cursor: 'pointer',
              padding: '0',
              opacity: 0.7
            }}
            onMouseOver={(e) => e.target.style.opacity = '1'}
            onMouseOut={(e) => e.target.style.opacity = '0.7'}
            title="Reset memory for this course"
          >
            Reset Memory
          </button>
        ) : (
          <div style={{ height: '16px' }}></div>
        )}
      </div>

    </div>
  );
};

export default App;